import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { Eye, EyeOff, Settings, Users, Calculator, CreditCard, PlayCircle } from 'lucide-react';
import LanguageSelector from './LanguageSelector';
import { useLanguage } from '@/contexts/LanguageContext';
import ROICalculator from './ROICalculator';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Login({ onLogin }) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [loginForm, setLoginForm] = useState({ email: '', password: '', company_code: '' });
  const [registerForm, setRegisterForm] = useState({ email: '', password: '', role: 'Maintenance Supervisor', customRole: '', company_code: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [verificationStep, setVerificationStep] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [sentCode, setSentCode] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  const [showROICalculator, setShowROICalculator] = useState(false);

  // Resend timer countdown
  React.useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  const sendVerificationCode = async (email) => {
    try {
      const response = await axios.post(`${API}/auth/send-verification-code`, { email });
      setResendTimer(60); // 60 second cooldown
      toast.success('Verification code sent!', {
        description: `Check your email: ${email}`,
        duration: 5000
      });
      return true;
    } catch (error) {
      console.error('Failed to send verification code:', error);
      toast.error('Failed to send verification code', {
        description: 'Please try again',
        duration: 5000
      });
      return false;
    }
  };

  const verifyCode = async () => {
    try {
      setLoading(true);
      const response = await axios.post(`${API}/auth/verify-code`, {
        email: registerForm.email,
        code: verificationCode
      });
      
      if (response.data.verified) {
        // Now actually register the user
        await completeRegistration();
      }
    } catch (error) {
      console.error('Verification failed:', error);
      const message = error.response?.data?.detail || 'Invalid or expired code';
      setError(message);
      toast.error('Verification failed', {
        description: message,
        duration: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  const completeRegistration = async () => {
    try {
      const roleToSubmit = registerForm.role === 'Other' ? registerForm.customRole : registerForm.role;
      
      const response = await axios.post(`${API}/auth/register`, {
        email: registerForm.email,
        password: registerForm.password,
        role: roleToSubmit,
        company_code: registerForm.company_code || null
      });
      
      const { access_token, user } = response.data;
      toast.success('Account created successfully!', {
        description: 'Welcome to Composys MaintPro',
        duration: 3000
      });
      onLogin(user, access_token);
    } catch (error) {
      console.error('Registration error:', error);
      const message = error.response?.data?.detail || 'Registration failed';
      setError(message);
      toast.error('Registration failed', {
        description: message,
        duration: 5000
      });
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/auth/login`, loginForm);
      const { access_token, user } = response.data;
      onLogin(user, access_token);
    } catch (error) {
      console.error('Login error:', error.response?.data);
      
      let message = 'Wrong username or password';
      
      // Check for specific error messages from backend
      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          message = error.response.data.detail;
        }
      }
      
      setError(message);
      
      // Show toast notification
      toast.error('Wrong email or password', {
        description: 'Please check your credentials and try again.',
        duration: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Use custom role if "Other" is selected, otherwise use selected role
      const roleToSubmit = registerForm.role === 'Other' ? registerForm.customRole : registerForm.role;
      
      // Validate custom role if "Other" is selected
      if (registerForm.role === 'Other' && !registerForm.customRole.trim()) {
        setError('Please specify your role');
        toast.error('Please specify your role');
        setLoading(false);
        return;
      }
      
      // Send verification code instead of directly registering
      const sent = await sendVerificationCode(registerForm.email);
      if (sent) {
        setVerificationStep(true);
      }
    } catch (error) {
      console.error('Registration error:', error);
      setError(error.response?.data?.detail || 'Registration failed. Please try again.');
      toast.error('Registration failed', {
        description: error.response?.data?.detail || 'Please try again',
        duration: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-[#E1F4FA]">
      {/* Language selector - top right corner */}
      <div className="absolute top-4 right-4 z-10">
        <LanguageSelector />
      </div>

      {/* ROI Calculator Modal */}
      <ROICalculator isOpen={showROICalculator} onClose={() => setShowROICalculator(false)} />

      {/* Floating Action Buttons */}
      <div className="fixed bottom-6 right-6 z-20 flex flex-col gap-3 animate-in slide-in-from-bottom duration-500">
        {/* Watch Demo Button */}
        <button
          onClick={() => window.open('https://youtu.be/WX2GpRzhjQk?si=fLtvXqeKxIKBItMB', '_blank')}
          className="group bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 text-white px-6 py-4 rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 flex items-center gap-3 hover:scale-105"
        >
          <div className="flex flex-col items-start">
            <span className="text-xs font-medium opacity-90">See it in action</span>
            <span className="text-sm font-bold flex items-center gap-2">
              <PlayCircle className="w-4 h-4" />
              Watch 1-Minute Demo
            </span>
          </div>
        </button>

        {/* View Plans Button */}
        <button
          onClick={() => navigate('/pricing')}
          className="group bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-6 py-4 rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 flex items-center gap-3 hover:scale-105"
        >
          <div className="flex flex-col items-start">
            <span className="text-xs font-medium opacity-90">{t('exploreOurPricing')}</span>
            <span className="text-sm font-bold flex items-center gap-2">
              <CreditCard className="w-4 h-4" />
              {t('viewPlans')}
            </span>
          </div>
        </button>

        {/* ROI Calculator Button */}
        <button
          onClick={() => setShowROICalculator(true)}
          className="group bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-6 py-4 rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 flex items-center gap-3 hover:scale-105"
        >
          <div className="flex flex-col items-start">
            <span className="text-xs font-medium opacity-90">{t('notSureYet')}</span>
            <span className="text-sm font-bold flex items-center gap-2">
              <Calculator className="w-4 h-4" />
              {t('calculateROI')}
            </span>
          </div>
        </button>
      </div>

      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-200 to-indigo-200 rounded-full opacity-20 blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-indigo-200 to-purple-200 rounded-full opacity-20 blur-3xl"></div>
      </div>

      <div className="relative w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl mb-4 shadow-lg">
            <Settings className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Composys MaintPro</h1>
          <p className="text-gray-600 mb-3">{t('maintenanceAndRepairScheduling')}</p>
          <p className="text-sm text-gray-500 max-w-lg mx-auto leading-relaxed">
            {t('cmmsTagline')}
          </p>
        </div>

        <Card className="glass border-0 shadow-2xl">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-semibold text-gray-900">{t('welcome')}</CardTitle>
            <CardDescription className="text-gray-600">
              {t('signInDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="login" className="space-y-6">
              <TabsList className="grid w-full grid-cols-2 bg-gray-100">
                <TabsTrigger 
                  value="login" 
                  className="data-[state=active]:bg-white data-[state=active]:text-blue-600"
                  data-testid="login-tab"
                >
                  {t('signIn')}
                </TabsTrigger>
                <TabsTrigger 
                  value="register" 
                  className="data-[state=active]:bg-white data-[state=active]:text-blue-600"
                  data-testid="register-tab"
                >
                  {t('signUp')}
                </TabsTrigger>
              </TabsList>

              {error && (
                <Alert className="border-red-200 bg-red-50">
                  <AlertDescription className="text-red-700">
                    {error}
                  </AlertDescription>
                </Alert>
              )}

              <TabsContent value="login" className="space-y-4">
                {error && (
                  <Alert className="border-red-200 bg-red-50">
                    <AlertDescription className="text-red-700">
                      {error}
                    </AlertDescription>
                  </Alert>
                )}
                <form onSubmit={handleLogin} className="space-y-4">
                  {error && (
                    <Alert variant="destructive" className="mb-4">
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}
                  
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                      Email
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder={t('emailPlaceholder')}
                      value={loginForm.email}
                      onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                      required
                      className="bg-white border-gray-200 focus:border-blue-400 focus:ring-blue-400"
                      data-testid="login-email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-sm font-medium text-gray-700">
                      {t('password')}
                    </Label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder={t('passwordPlaceholder')}
                        value={loginForm.password}
                        onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                        required
                        className="bg-white border-gray-200 focus:border-blue-400 focus:ring-blue-400 pr-10"
                        data-testid="login-password"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 p-0 hover:bg-transparent"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4 text-gray-500" />
                        ) : (
                          <Eye className="h-4 w-4 text-gray-500" />
                        )}
                      </Button>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="company-code" className="text-sm font-medium text-gray-700">
                      {t('companyCode')} <span className="text-gray-500">({t('optional')})</span>
                    </Label>
                    <Input
                      id="company-code"
                      type="text"
                      placeholder={t('companyCodePlaceholder')}
                      value={loginForm.company_code}
                      onChange={(e) => setLoginForm({ ...loginForm, company_code: e.target.value })}
                      className="bg-white border-gray-200 focus:border-blue-400 focus:ring-blue-400"
                    />
                    <p className="text-xs text-gray-500">{t('companyCodeHint')}</p>
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-medium py-2.5 shadow-lg btn-animation"
                    disabled={loading}
                    data-testid="login-submit-btn"
                  >
                    {loading ? (
                      <div className="flex items-center space-x-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>{t('signingIn')}</span>
                      </div>
                    ) : (
                      t('loginButton')
                    )}
                  </Button>
                  
                  <div className="text-center mt-4">
                    <a 
                      href="/forgot-password" 
                      className="text-sm text-blue-600 hover:text-blue-700 hover:underline"
                    >
                      {t('forgotPassword')}
                    </a>
                  </div>
                </form>
              </TabsContent>

              <TabsContent value="register" className="space-y-4">
                {!verificationStep ? (
                  <form onSubmit={handleRegister} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="reg-email" className="text-sm font-medium text-gray-700">
                      Email
                    </Label>
                    <Input
                      id="reg-email"
                      type="email"
                      placeholder={t('emailPlaceholder')}
                      value={registerForm.email}
                      onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                      required
                      className="bg-white border-gray-200 focus:border-blue-400 focus:ring-blue-400"
                      data-testid="register-email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reg-role" className="text-sm font-medium text-gray-700">
                      Role
                    </Label>
                    <Select 
                      value={registerForm.role} 
                      onValueChange={(value) => setRegisterForm({ ...registerForm, role: value, customRole: '' })}
                    >
                      <SelectTrigger className="bg-white border-gray-200 focus:border-blue-400 focus:ring-blue-400" data-testid="register-role">
                        <SelectValue placeholder="Select role" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Admin">Admin</SelectItem>
                        <SelectItem value="Maintenance Supervisor">Maintenance Supervisor</SelectItem>
                        <SelectItem value="Supervisor">Supervisor</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Custom role input - shows when "Other" is selected */}
                  {registerForm.role === 'Other' && (
                    <div className="space-y-2">
                      <Label htmlFor="custom-role" className="text-sm font-medium text-gray-700">
                        Specify Your Role
                      </Label>
                      <Input
                        id="custom-role"
                        type="text"
                        placeholder="Enter your role"
                        value={registerForm.customRole}
                        onChange={(e) => setRegisterForm({ ...registerForm, customRole: e.target.value })}
                        required
                        className="bg-white border-gray-200 focus:border-blue-400 focus:ring-blue-400"
                        data-testid="custom-role-input"
                      />
                    </div>
                  )}
                  
                  <div className="space-y-2">
                    <Label htmlFor="company-code" className="text-sm font-medium text-gray-700">
                      Company Code (Optional)
                    </Label>
                    <Input
                      id="company-code"
                      type="text"
                      placeholder="Leave empty to create new company"
                      value={registerForm.company_code}
                      onChange={(e) => setRegisterForm({ ...registerForm, company_code: e.target.value })}
                      className="bg-white border-gray-200 focus:border-blue-400 focus:ring-blue-400"
                    />
                    <p className="text-xs text-gray-500">
                      Have a company code from your team? Enter it to join their workspace.
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="reg-password" className="text-sm font-medium text-gray-700">
                      Password
                    </Label>
                    <div className="relative">
                      <Input
                        id="reg-password"
                        type={showRegisterPassword ? 'text' : 'password'}
                        placeholder="Create a password"
                        value={registerForm.password}
                        onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                        required
                        className="bg-white border-gray-200 focus:border-blue-400 focus:ring-blue-400 pr-10"
                        data-testid="register-password"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 p-0 hover:bg-transparent"
                        onClick={() => setShowRegisterPassword(!showRegisterPassword)}
                      >
                        {showRegisterPassword ? (
                          <EyeOff className="h-4 w-4 text-gray-500" />
                        ) : (
                          <Eye className="h-4 w-4 text-gray-500" />
                        )}
                      </Button>
                    </div>
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-medium py-2.5 shadow-lg btn-animation"
                    disabled={loading}
                    data-testid="register-submit-btn"
                  >
                    {loading ? (
                      <div className="flex items-center space-x-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Creating Account...</span>
                      </div>
                    ) : (
                      'Create Account'
                    )}
                  </Button>
                </form>
                ) : (
                  <div className="space-y-4">
                    <div className="text-center space-y-2">
                      <h3 className="text-lg font-semibold">Verify Your Email</h3>
                      <p className="text-sm text-gray-600">
                        We sent a 6-digit code to <strong>{registerForm.email}</strong>
                      </p>
                      <p className="text-xs text-gray-500">
                        Check your inbox and spam folder
                      </p>
                    </div>
                    
                    {error && (
                      <Alert variant="destructive">
                        <AlertDescription>{error}</AlertDescription>
                      </Alert>
                    )}
                    
                    <div className="space-y-2">
                      <Label htmlFor="verification-code">Verification Code</Label>
                      <Input
                        id="verification-code"
                        type="text"
                        placeholder="Enter 6-digit code"
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        maxLength={6}
                        className="text-center text-2xl tracking-widest"
                        required
                      />
                    </div>
                    
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setVerificationStep(false)}
                        className="flex-1"
                      >
                        Back
                      </Button>
                      <Button
                        type="button"
                        onClick={verifyCode}
                        disabled={verificationCode.length !== 6 || loading}
                        className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600"
                      >
                        {loading ? 'Verifying...' : 'Verify & Create Account'}
                      </Button>
                    </div>
                    
                    <div className="text-center">
                      <Button
                        type="button"
                        variant="link"
                        onClick={() => sendVerificationCode(registerForm.email)}
                        disabled={resendTimer > 0}
                        className="text-sm"
                      >
                        {resendTimer > 0 ? `Resend code in ${resendTimer}s` : 'Resend code'}
                      </Button>
                    </div>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        <div className="text-center mt-6 text-sm text-gray-500">
          {t('start14DayTrial')}
        </div>
      </div>
    </div>
  );
}

export default Login;
